import { NoticeMapper, NoticeType, Notice } from '@acp/shared';
import { useMemo } from 'react';

export function useActiveNotices(notice: Notice, {
    propertyId,
    address,
    isIntegrated,
}: {
    propertyId: number;
    address: string;
    isIntegrated: boolean;
}) {
    const activeNotice = useMemo(() => {
        if (notice === null) return null;

        return notice;
    }, [notice]);

    const noticeTypeDescription = NoticeMapper.getNoticeTypeDescription(activeNotice?.noticeType);

    const activeNoticeDescription = useMemo(() => {
        switch (activeNotice?.noticeType) {
            case NoticeType.RequiresAgencyConfirmation:
                return 'The tenant requires agency confirmation before allowing access.';
            case NoticeType.RefusingService:
                return 'The tenant is refusing Detector Inspector access to the property.';
            case NoticeType.CommunicationDifficulty:
                return 'There is a language barrier with the tenant.';
            case NoticeType.NoTenantDetails:
                return 'We require tenant details to be added.';
            case NoticeType.SameTenantDetails:
                return 'We require tenant details to be updated or confirmed.';
            case NoticeType.TenantRequiresAgencyAttendance:
                return 'The tenant requires agency attendance.';
            case NoticeType.TenantUnavailableBusinessHours:
                return 'The tenant is unavailable during business hours.';
            case NoticeType.RequireUpdateDetailXDays:
                return 'The property has required updated tenant details for over 30 days.';
            case NoticeType.TenantCancelledConsecutively:
            case NoticeType.Unknown:
            case NoticeType.BookingProblemOther:
                return 'We have been unable to book this property';
            case NoticeType.UnableToContactOccupant:
            case NoticeType.UnableToContactOccupantAuto:
                return 'We are unable to contact the occupant.';
            case NoticeType.AccessProblemBadKeys:
                return 'We are unable to access the property as the key used by our technician did not work.';
            default:
                return 'There is an access issue with this property.';
        }
    }, [activeNotice]);

    const link = useMemo(() => {
        switch (activeNotice?.noticeType) {
            case NoticeType.RequiresAgencyConfirmation:
            case NoticeType.RefusingService:
            case NoticeType.CommunicationDifficulty:
            case NoticeType.TenantRequiresAgencyAttendance:
            case NoticeType.TenantUnavailableBusinessHours:
            case NoticeType.AccessProblemBadKeys:
            case NoticeType.UnableToContactOccupant:
            case NoticeType.UnableToContactOccupantAuto:
                return `/Notices/view?search=${address}`;
            case NoticeType.NoTenantDetails:
            case NoticeType.SameTenantDetails:
            case NoticeType.RequireUpdateDetailXDays:
                if (isIntegrated === false) {
                    return `/Properties/view/${propertyId}`;
                }

                return null;
            default:
                return null;
        }
    }, [
        address,
        propertyId,
        isIntegrated,
        activeNotice,
    ]);

    const description = useMemo(() => {
        switch (activeNotice?.noticeType) {
            case NoticeType.RequiresAgencyConfirmation:
            case NoticeType.RefusingService:
            case NoticeType.CommunicationDifficulty:
            case NoticeType.TenantRequiresAgencyAttendance:
            case NoticeType.TenantUnavailableBusinessHours:
            case NoticeType.UnableToContactOccupant:
            case NoticeType.UnableToContactOccupantAuto:
            case NoticeType.AccessProblemBadKeys:
                return 'Resolve issue';
            case NoticeType.NoTenantDetails:
            case NoticeType.SameTenantDetails:
            case NoticeType.RequireUpdateDetailXDays:
                if (isIntegrated) {
                    return 'Update software';
                }
                return 'Update lease details';
            case NoticeType.TenantCancelledConsecutively:
            case NoticeType.Unknown:
            case NoticeType.BookingProblemOther:
                return 'Contact us';
            default:
                return null;
        }
    }, [activeNotice, isIntegrated]);

    return {
        activeNotice,
        activeNoticeDescription,
        noticeTypeDescription,
        action: {
            link,
            description,
        },
    };
}
